import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import { Modal } from '../ui/modal';
import Button from '../ui/button/Button';

interface BillingModalProps {
    billing?: any;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export default function BillingModal({ billing, isOpen, onClose, onSave }: BillingModalProps) {
    const [form, setForm] = useState({
        name: '',
        company: '',
        address_1: '',
        address_2: '',
        city: '',
        post_code: '',
        uae_id: '',
        save: true
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (billing) {
            setForm({
                name: billing.name || '',
                company: billing.company || '',
                address_1: billing.address_1 || '',
                address_2: billing.address_2 || '',
                city: billing.city || '',
                post_code: billing.post_code || '',
                uae_id: billing.uae_id || '',
                save: !!billing.save
            });
        } else {
            setForm({
                name: '',
                company: '',
                address_1: '',
                address_2: '',
                city: '',
                post_code: '',
                uae_id: '',
                save: true
            });
        }
    }, [billing, isOpen]);

    const handleSave = async () => {
        if (!form.name || !form.address_1 || !form.city || !form.post_code || !form.uae_id) {
            Swal.fire({
                title: 'Warning',
                text: 'Please fill in all required fields',
                icon: 'warning'
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...form,
                uae_id: form.uae_id ? parseInt(form.uae_id as string) : 0 // ensure integer or 0
            };

            if (billing?.id) {
                await client.postApiBillingBillingDetailsUpdate({
                    postApiBillingBillingDetailsUpdateRequest: {
                        id: billing.id,
                        ...payload
                    } as any
                });
            } else {
                await client.postApiBillingBillingDetailsCreate({
                    postApiBillingBillingDetailsCreateRequest: payload as any
                });
            }
            onSave();
            onClose();
        } catch (err: any) {
            console.error('Failed to save billing details:', err);
            Swal.fire({
                title: 'Error',
                text: err.response?.data?.message || err.message || 'Failed to save',
                icon: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl">
            <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden max-h-[90vh]">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {billing ? 'Edit Billing Details' : 'Add Billing Details'}
                    </h3>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Name *</label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Company</label>
                            <input
                                type="text"
                                value={form.company}
                                onChange={e => setForm({ ...form, company: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">UAE ID *</label>
                            <input
                                type="number"
                                value={form.uae_id}
                                onChange={e => setForm({ ...form, uae_id: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Post Code *</label>
                            <input
                                type="text"
                                value={form.post_code}
                                onChange={e => setForm({ ...form, post_code: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Address 1 *</label>
                        <input
                            type="text"
                            value={form.address_1}
                            onChange={e => setForm({ ...form, address_1: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Address 2</label>
                        <input
                            type="text"
                            value={form.address_2}
                            onChange={e => setForm({ ...form, address_2: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">City *</label>
                        <input
                            type="text"
                            value={form.city}
                            onChange={e => setForm({ ...form, city: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                    </div>

                    <div className="flex items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            checked={form.save}
                            onChange={e => setForm({ ...form, save: e.target.checked })}
                            className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Save this address</label>
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancel</Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
